import { Send } from "@mui/icons-material";
import {
  Avatar,
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, KeyboardEvent, useState } from "react";
import { Author } from "../../../manifest/models/author.models";
import { getUsernameFor } from "../../../utils/username";

interface CommentBarProps {
  visible: boolean;
  user: Author;
  onSubmitted: (comment: string) => void;
}

export default function CommentsReply(props: CommentBarProps) {
  if (!props.visible) return null;
  const { user, onSubmitted: onCommentSubmit } = props;
  const { avatar } = user;
  const name = getUsernameFor(user);
  const [comment, setComment] = useState<string>("");

  const MAX_LENGTH = 280;
  const isOverLimit = comment.length > MAX_LENGTH;

  const handleSend = (): void => {
    if (comment.trim() && !isOverLimit) {
      onCommentSubmit(comment);
      setComment("");
    }
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    setComment(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Avatar src={avatar.filename} sx={{ width: 32, height: 32, mt: 0.5 }} />

        <TextField
          fullWidth
          multiline
          placeholder={`Comment as ${name}`}
          variant="outlined"
          size="small"
          value={comment}
          maxRows={6}
          minRows={1}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          error={isOverLimit}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ alignSelf: "flex-end", mb: 0.5 }}
                >
                  {comment.trim() && (
                    <IconButton
                      size="small"
                      onClick={handleSend}
                      disabled={isOverLimit}
                      color="primary"
                      aria-label="send comment"
                      sx={{ opacity: isOverLimit ? 0.3 : 1 }}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
              backgroundColor: isOverLimit ? "#fff1f0" : "#f0f2f5",
              paddingRight: "8px",
              transition: "background-color 0.2s ease",
              "& fieldset": { border: "none" },
              "&:hover fieldset": { border: "none" },
              "&.Mui-focused fieldset": {
                border: isOverLimit ? "1px solid red" : "none",
              },
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mr: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: isOverLimit ? "error.main" : "text.disabled",
            fontWeight: isOverLimit ? 800 : 500,
            fontSize: isOverLimit ? "0.8rem" : "0.75rem",
            transition: "all 0.2s ease",
          }}
        >
          {comment.length} / {MAX_LENGTH}
        </Typography>
      </Box>
    </Box>
  );
}
